import { AlarmGroup, OutputConsoleSetting, getOutputSettingTypeString } from 'icreate/common/models/alarm-group';
import { ChannelType, isDigital } from 'icreate/common/models/channel-type';
import { DutyEngineer } from 'icreate/common/models/duty-engineer';
import { getPrintStrings } from 'icreate/common/models/motor-alarm-setting';
import { OutputSetting, SourceChannelCondition } from 'icreate/common/models/output-settings';
import { SensorCableTag } from 'icreate/common/models/sensor-cable-tag';
import {
    DIRows,
    DONormalRows,
    DORelayRows,
    AIRows,
    ThermalKRows,
    ComWireRows,
    OutputRows,
    getTerminalSensors,
    joinTerminalChannelTags,
} from 'icreate/common/models/sensor-cable-tag-print';
import { SlotOptionType, terminalType, TerminalType } from 'icreate/common/models/slot';

export const excelDIColumnLength = 11;
export const excelDONormalColumnLength = 10;
export const excelDORelayColumnLength = 9;
export const excelAIColumnLength = 13;
export const excelOutputColumnLength = 9;
export const excelThermalColumnLength = 12;

export function prepareExcelData(
    sensorCableTag: SensorCableTag[],
    slotType: SlotOptionType,
    outputType: TerminalType,
    outputStartPin: number,
    dutyEngineers: DutyEngineer[],
    digitalOutputSettings: OutputSetting[],
    analogOutputSettings: OutputSetting[],
    consoleSettings: (OutputConsoleSetting & { AlarmGroup: AlarmGroup })[]
) {
    if (slotType == 'DI') {
        return getDIRows(sensorCableTag, dutyEngineers);
    } else if (slotType == 'DO') {
        return getDORows(sensorCableTag, outputType, outputStartPin, digitalOutputSettings, consoleSettings);
    } else if (slotType == 'AI (Pt100 3-wire)') {
        return getAI3TypeRows(sensorCableTag);
    } else if (slotType == 'AI (4-20mA)' || slotType == 'AI (Pt100 2-wire)' || slotType == 'AI (1-5V)') {
        return getAITypeRows(sensorCableTag);
    } else if (slotType == 'AI (Thermal K)') {
        return getThermalKTypeRows(sensorCableTag);
    } else if (slotType == 'AO (4-20mA)') {
        return getOutputTypeRows(sensorCableTag, analogOutputSettings);
    }
    return [[]];
}

function getDIRows(sensorCableTag: SensorCableTag[], dutyEngineers: DutyEngineer[]): any {
    const rows = [];
    for (let i = 0; i < DIRows; i++) {
        const { sourceSensor, runningHourSensor } = getTerminalSensors(sensorCableTag, i + 1);
        let sensor = sourceSensor ?? runningHourSensor;
        let runningHourTag = sourceSensor ? runningHourSensor?.channel?.fcuData?.fcuChannelId : '';
        const engineer = dutyEngineers?.find(eng => eng.terminal == i + 1);
        let row = new Array(excelDIColumnLength).fill('');
        row[0] = sensor?.id ?? '';
        if (engineer) {
            row[1] = '';
            row[2] = `Duty - ${engineer.name}`;
            row[3] = 'ON/OFF';
        } else if (sensor?.channel) {
            const channel = sensor.channel;
            const isMotorChannel = channel?.fcuData?.channelType == ChannelType.DIGITAL_MOTOR;
            const motorSettings = channel?.fcuData?.motorChannelSetting;
            const statusStrings = getPrintStrings(motorSettings);
            if (isMotorChannel && statusStrings) {
                for (let j = 0; j < statusStrings.length; j++) {
                    if (j > 0) {
                        const nextTerminal = getTerminalSensors(sensorCableTag, i + 1 + j);
                        sensor = nextTerminal.sourceSensor;
                        runningHourTag = nextTerminal.runningHourSensor?.channel?.fcuData?.fcuChannelId;
                        row = new Array(excelDIColumnLength).fill('');
                    }
                    row[0] = sensor?.id ?? '';
                    row[1] = joinTerminalChannelTags(channel.fcuData?.fcuChannelId, runningHourTag);
                    row[2] = channel.opsData?.name ?? '';
                    row[3] = `${statusStrings[j]?.status}`;
                    row[4] = i + j + 1;
                    row[5] = sensor?.wireMark?.[0] ?? '';
                    row[6] = i + j + 1;
                    row[7] = sensor?.wireMarkClass?.[0] ?? '';
                    row[8] = sensor?.coreNoIn?.[0] ?? '';
                    row[9] = sensor?.coreNoCom?.[0] ?? '';
                    row[10] = sensor?.dist?.[0] ?? '';
                    rows.push(row);
                }
                if (statusStrings.length === 0) continue;
                i += statusStrings.length - 1;
                continue;
            } else {
                let statusString;
                const channelType = channel.fcuData.channelType;
                const isDigitalChannel = channelType && isDigital(channelType);
                if (isDigitalChannel) {
                    statusString =
                        channel.fcuData?.digitalAlarmSetting?.statusStringContactOn +
                        '/' +
                        channel.fcuData?.digitalAlarmSetting?.statusStringContactOff;
                } else {
                    let alarmStrings = [];
                    const enabledSettings = [
                        channel.fcuData?.alarmSettingVeryHigh,
                        channel.fcuData?.alarmSettingHigh,
                        channel.fcuData?.alarmSettingLow,
                        channel.fcuData?.alarmSettingVeryLow,
                    ].filter(setting => setting.enable);

                    if (enabledSettings && enabledSettings.length > 0) {
                        alarmStrings = enabledSettings.map(setting => setting.statusString);
                    }
                    if (alarmStrings.length > 0) statusString = alarmStrings.join('/');
                }
                row[1] = joinTerminalChannelTags(channel.fcuData?.fcuChannelId, runningHourTag);
                row[2] = channel.opsData?.name ?? '';
                row[3] = statusString ?? '';
            }
        }
        row[4] = i + 1;
        row[5] = sensor?.wireMark?.[0] ?? '';
        row[6] = i + 1;
        row[7] = sensor?.wireMarkClass?.[0] ?? '';
        row[8] = sensor?.coreNoIn?.[0] ?? '';
        row[9] = sensor?.coreNoCom?.[0] ?? '';
        row[10] = sensor?.dist?.[0] ?? '';

        rows.push(row);
    }

    return rows;
}

function getDORows(
    sensorCableTag: SensorCableTag[],
    outputType: TerminalType,
    outputStartPin: number,
    digitalOutputSettings: OutputSetting[],
    consoleSettings: (OutputConsoleSetting & { AlarmGroup: AlarmGroup })[]
) {
    const rows = [];

    if (outputType == terminalType.normal) {
        for (let i = outputStartPin; i < outputStartPin + DONormalRows; i++) {
            const sensor = sensorCableTag.find(item => Number(item.pinNum) === i + 1);
            const row = new Array(excelDONormalColumnLength).fill('');
            const digitalSetting = digitalOutputSettings.find(setting => setting.terminalNum == i + 1);
            const consoleSetting = consoleSettings.find(setting => setting.terminalNum == i + 1);
            let name = '';
            let tag = '';
            if (digitalSetting) {
                const channels = digitalSetting.channels;
                if (channels.length > 1) {
                    tag = digitalSetting.sourceChannelCondition == SourceChannelCondition.AND ? 'AND' : 'OR';
                    const channelsNameArray = [];
                    let extraCounter = 0;
                    for (let i = 0; i < channels.length; i++) {
                        if (i == 0) {
                            const channelTag = channels[i]?.channel?.fcuData?.fcuChannelId ?? '';
                            channelsNameArray.push(`CH No. ${channelTag}`);
                        } else if (i <= 2) {
                            const channelTag = channels[i]?.channel?.fcuData?.fcuChannelId ?? '';
                            channelsNameArray.push(`${channelTag}`);
                        } else extraCounter += 1;
                    }
                    name = channelsNameArray.join(', ');
                    if (extraCounter) name = name + ` (+${extraCounter})`;
                } else {
                    const sourceChannel = channels[0];
                    tag = sourceChannel?.channel?.fcuData?.fcuChannelId ?? '';
                    name = sourceChannel?.channel?.opsData?.name ?? '';
                }
            } else if (consoleSetting) {
                const alarmGroup: AlarmGroup = consoleSetting.AlarmGroup;
                const type = getOutputSettingTypeString(consoleSetting?.type);
                name = alarmGroup.name + ` (${type})`;
            }
            row[0] = sensor?.id ?? '';
            row[1] = tag;
            row[2] = name;
            row[3] = i + 1;
            row[4] = sensor?.wireMark?.[0] ?? '';
            row[5] = i + 1;
            row[6] = sensor?.wireMarkClass?.[0] ?? '';
            row[7] = sensor?.coreNoIn?.[0] ?? '';
            row[8] = sensor?.coreNoCom?.[0] ?? '';
            row[9] = sensor?.dist?.[0] ?? '';

            rows.push(row);
        }
    } else if (outputType == terminalType.relay) {
        for (let i = outputStartPin; i < outputStartPin + DORelayRows; i++) {
            const sensor = sensorCableTag.find(item => Number(item.pinNum) === i + 1);
            const digitalSetting = digitalOutputSettings.find(setting => setting.terminalNum == i + 1);
            const consoleSetting = consoleSettings.find(setting => setting.terminalNum == i + 1);
            let name = '';
            let tag = '';
            if (digitalSetting) {
                const channels = digitalSetting.channels;
                if (channels.length > 1) {
                    tag = digitalSetting.sourceChannelCondition == SourceChannelCondition.AND ? 'AND' : 'OR';
                    const channelsNameArray = [];
                    let extraCounter = 0;
                    for (let i = 0; i < channels.length; i++) {
                        if (i == 0) {
                            const channelTag = channels[i]?.channel?.fcuData?.fcuChannelId ?? '';
                            channelsNameArray.push(`CH No. ${channelTag}`);
                        } else if (i <= 2) {
                            const channelTag = channels[i]?.channel?.fcuData?.fcuChannelId ?? '';
                            channelsNameArray.push(`${channelTag}`);
                        } else extraCounter += 1;
                    }
                    name = channelsNameArray.join(', ');
                    if (extraCounter) name = name + ` (+${extraCounter})`;
                } else {
                    const sourceChannel = channels[0];
                    tag = sourceChannel?.channel?.fcuData?.fcuChannelId ?? '';
                    name = sourceChannel?.channel?.opsData?.name ?? '';
                }
            } else if (consoleSetting) {
                const alarmGroup: AlarmGroup = consoleSetting.AlarmGroup;
                const type = getOutputSettingTypeString(consoleSetting?.type);
                name = alarmGroup.name + ` (${type})`;
            }
            for (let j = 0; j < 3; j++) {
                const row = new Array(excelDORelayColumnLength).fill('');
                const letter = j === 0 ? 'A' : j === 1 ? 'B' : 'C';
                row[0] = sensor?.id ?? '';
                row[1] = j == 0 ? tag : '';
                row[2] = j == 0 ? name : '';
                row[3] = j == 0 ? i + 1 : '';
                row[4] = sensor?.wireMark?.[j] ?? '';
                row[5] = letter + (i - outputStartPin + 1);
                row[6] = sensor?.outCoreNo?.[j] ?? '';
                row[7] = sensor?.wireMarkClass?.[j] ?? '';
                row[8] = sensor?.dist?.[j] ?? '';

                rows.push(row);
            }
        }
    }
    return rows;
}

function getAI3TypeRows(sensorCableTag: SensorCableTag[]) {
    const rows = [];

    for (let i = 0; i < AIRows; i++) {
        const sensor = sensorCableTag.find(item => Number(item.pinNum) === i + 1);

        for (let j = 0; j < 3; j++) {
            const row = new Array(excelAIColumnLength).fill('');
            row[0] = sensor?.id ?? '';
            const channel = sensor?.channel;
            if (channel && j == 0) {
                let statusString;
                const channelType = channel.fcuData.channelType;
                const isDigitalChannel = channelType && isDigital(channelType);
                if (isDigitalChannel) {
                    statusString =
                        channel.fcuData?.digitalAlarmSetting?.statusStringContactOn +
                        '/' +
                        channel.fcuData?.digitalAlarmSetting?.statusStringContactOff;
                } else {
                    let alarmStrings = [];
                    const enabledSettings = [
                        channel.fcuData?.alarmSettingVeryHigh,
                        channel.fcuData?.alarmSettingHigh,
                        channel.fcuData?.alarmSettingLow,
                        channel.fcuData?.alarmSettingVeryLow,
                    ].filter(setting => setting.enable);

                    if (enabledSettings && enabledSettings.length > 0) {
                        alarmStrings = enabledSettings.map(setting => setting.statusString);
                    }

                    if (alarmStrings.length > 0) {
                        statusString = alarmStrings.join('/');
                    }
                }
                row[1] = channel.fcuData?.fcuChannelId ?? '';
                row[2] = channel.opsData?.name ?? '';
                row[3] = statusString ?? '';
                row[4] = channel.fcuData?.measurableRange?.min + ' - ' + channel.fcuData?.measurableRange?.max;
                row[5] = channel.opsData?.measurementUnit ?? '';
            }
            row[6] = i + 1;
            row[7] = sensor?.wireMark?.[j] ?? '';
            row[8] = i + 1;
            row[9] = sensor?.wireMarkClass?.[j] ?? '';
            row[10] = sensor?.coreNoIn?.[j] ?? '';
            row[11] = sensor?.coreNoCom?.[j] ?? '';
            row[12] = sensor?.dist?.[j] ?? '';

            rows.push(row);
        }
    }

    return rows;
}

function getAITypeRows(sensorCableTag: SensorCableTag[]) {
    const rows = [];
    for (let i = 0; i < AIRows; i++) {
        const sensor = sensorCableTag.find(item => Number(item.pinNum) === i + 1);

        const row = new Array(excelAIColumnLength).fill('');
        row[0] = sensor?.id ?? '';
        const channel = sensor?.channel;
        if (channel) {
            let statusString;
            const channelType = channel.fcuData.channelType;
            const isDigitalChannel = channelType && isDigital(channelType);
            if (isDigitalChannel) {
                statusString =
                    channel.fcuData?.digitalAlarmSetting?.statusStringContactOn +
                    '/' +
                    channel.fcuData?.digitalAlarmSetting?.statusStringContactOff;
            } else {
                let alarmStrings = [];
                const enabledSettings = [
                    channel.fcuData?.alarmSettingVeryHigh,
                    channel.fcuData?.alarmSettingHigh,
                    channel.fcuData?.alarmSettingLow,
                    channel.fcuData?.alarmSettingVeryLow,
                ].filter(setting => setting.enable);

                if (enabledSettings && enabledSettings.length > 0) {
                    alarmStrings = enabledSettings.map(setting => setting.statusString);
                }

                if (alarmStrings.length > 0) {
                    statusString = alarmStrings.join('/');
                }
            }
            row[1] = channel.fcuData?.fcuChannelId ?? '';
            row[2] = channel.opsData?.name ?? '';
            row[3] = statusString ?? '';
            row[4] = channel.fcuData?.measurableRange?.min + ' - ' + channel.fcuData?.measurableRange?.max;
            row[5] = channel.opsData?.measurementUnit ?? '';
        }
        row[6] = i + 1;
        row[7] = sensor?.wireMark?.[0] ?? '';
        row[8] = i + 1;
        row[9] = sensor?.wireMarkClass?.[0] ?? '';
        row[10] = sensor?.coreNoIn?.[0] ?? '';
        row[11] = sensor?.coreNoCom?.[0] ?? '';
        row[12] = sensor?.dist?.[0] ?? '';

        rows.push(row);
    }

    return rows;
}

function getThermalKTypeRows(sensorCableTag: SensorCableTag[]) {
    const rows = [];
    for (let i = 0; i < ThermalKRows + ComWireRows; i++) {
        let sensor;
        sensor = sensorCableTag.find(item => Number(item.pinNum) === i + 1);
        const row = new Array(excelThermalColumnLength).fill('');
        if (sensor && i < ThermalKRows) {
            row[0] = sensor.id;
            if (sensor.channel) {
                row[1] = sensor.channel.fcuData.fcuChannelId;
                row[2] = sensor.channel.opsData.name;
                row[3] =
                    sensor.channel.fcuData.measurableRange.min + ' - ' + sensor.channel.fcuData.measurableRange.max;
                row[4] = sensor.channel.opsData.measurementUnit;
            }
            row[5] = i + 1;
            row[6] = sensor.wireMark?.[0] ?? '';
            row[7] = i + 1;
            row[8] = sensor.wireMarkClass?.[0] ?? '';
            row[9] = sensor.coreNoIn?.[0] ?? '';
            row[10] = sensor.coreNoCom?.[0] ?? '';
            row[11] = sensor.dist?.[0] ?? '';
        } else if (sensor) {
            row[0] = sensor.id;
            row[5] = 'COM' + (i - ThermalKRows + 1);
            row[6] = sensor.wireMark?.[0] ?? '';
            row[7] = '';
            row[8] = sensor.wireMarkClass?.[0] ?? '';
            row[9] = sensor.coreNoIn?.[0] ?? '';
            row[10] = sensor.coreNoCom?.[0] ?? '';
            row[11] = sensor.dist?.[0] ?? '';
        } else if (!sensor && i < ThermalKRows) {
            row[5] = i + 1;
            row[7] = i + 1;
        } else {
            row[5] = 'COM' + (i - ThermalKRows + 1);
            row[7] = '';
        }

        rows.push(row);
    }

    return rows;
}

function getOutputTypeRows(sensorCableTag: SensorCableTag[], analogOutputSettings: OutputSetting[]) {
    const rows = [];
    for (let i = 0; i < OutputRows; i++) {
        const sensor = sensorCableTag.find(item => Number(item.pinNum) === i + 1);

        const analogSetting = analogOutputSettings?.find(setting => setting.terminalNum == i + 1);
        const analogChannel = analogSetting?.channels?.[0]?.channel;

        const row = new Array(excelOutputColumnLength).fill('');
        row[0] = sensor?.id ?? '';
        row[1] = analogChannel?.opsData?.name ?? '';
        row[2] = i + 1;
        row[3] = sensor?.wireMark?.[0] ?? '';
        row[4] = i + 1;
        row[5] = sensor?.wireMarkClass?.[0] ?? '';
        row[6] = sensor?.coreNoIn?.[0] ?? '';
        row[7] = sensor?.coreNoCom?.[0] ?? '';
        row[8] = sensor?.dist?.[0] ?? '';

        rows.push(row);
    }

    return rows;
}
